import * as React from 'react'

import { XIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import { PortalLoader } from '~/client/src/login/portal/components/PortalLoader'
import {
  authMutedLinkClassName,
  fieldErrorId,
  FieldLabelRow,
} from '~/client/src/shared/components/AuthLayout'
import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import { Field } from '~/client/src/shared/components/shadcn-ui/field'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import Localization from '~/client/src/shared/localization/LocalizationManager'

const CODE_INPUT_ID = 'login-otp-code'

interface ICodeInputProps {
  length: number
  onChange: (value: string) => void
  testId: string
  id?: string
  value?: string
  ariaLabel?: string
  ariaDescribedBy?: string
  isInvalid?: boolean
}

/** The SMS code field: digits only, and the OS can offer the texted code. */
export function CodeInput({
  length,
  onChange,
  testId,
  id,
  value,
  ariaLabel,
  ariaDescribedBy,
  isInvalid,
}: ICodeInputProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget
    input.value = input.value.replace(/\D/g, '').slice(0, length)
    onChange(input.value)
  }

  return (
    <Input
      id={id}
      autoFocus
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="one-time-code"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={isInvalid || undefined}
      className="tw:text-center tw:tracking-widest"
      value={value}
      onChange={handleChange}
      data-testid={testId}
    />
  )
}

interface IProps {
  phoneNumber: string
  isOpen: boolean
  errorMessage: string
  codeLength: number

  isCodeBeingVerified: boolean
  isCodeBeingResent: boolean

  onHide: () => void
  onCodeInputChange: (value: string) => void
  onResendCode: () => void
}

@observer
export default class CodeVerificationPopup extends React.Component<IProps> {
  public render() {
    const { isOpen, codeLength, phoneNumber } = this.props

    return (
      <Dialog open={isOpen} onOpenChange={this.handleOpenChange}>
        <DialogContent
          showCloseButton={false}
          data-testid="login-code-verification-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {Localization.translator.phoneVerification}
            </DialogTitle>
            <DialogDescription>
              {Localization.translator.enterTheXDigitCodeSentTo(codeLength)}{' '}
              <strong className="tw:text-foreground">{phoneNumber}</strong>
            </DialogDescription>
          </DialogHeader>

          {this.renderContent()}

          <DialogClose
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                className="tw:absolute tw:top-2 tw:right-2"
                aria-label={Localization.translator.close}
                data-testid="login-code-verification-close-button"
              />
            }
          >
            <XIcon />
          </DialogClose>
        </DialogContent>
      </Dialog>
    )
  }

  private renderContent() {
    const {
      isCodeBeingVerified,
      isCodeBeingResent,
      codeLength,
      errorMessage,
      onCodeInputChange,
      onResendCode,
    } = this.props

    if (isCodeBeingVerified) {
      return <PortalLoader hint={Localization.translator.codeVerifying} />
    }

    if (isCodeBeingResent) {
      return <PortalLoader hint={Localization.translator.codeResending} />
    }

    return (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <Field data-invalid={!!errorMessage}>
          <FieldLabelRow htmlFor={CODE_INPUT_ID} error={errorMessage}>
            {Localization.translator.code}
          </FieldLabelRow>
          <CodeInput
            id={CODE_INPUT_ID}
            length={codeLength}
            onChange={onCodeInputChange}
            testId="login-otp-code-input"
            ariaDescribedBy={
              errorMessage ? fieldErrorId(CODE_INPUT_ID) : undefined
            }
            isInvalid={!!errorMessage}
          />
        </Field>
        <Button
          type="button"
          variant="link"
          className={`tw:self-center ${authMutedLinkClassName}`}
          onClick={onResendCode}
          data-testid="login-resend-code-link"
        >
          {Localization.translator.resendCode}
        </Button>
      </div>
    )
  }

  private handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      this.props.onHide()
    }
  }
}
